import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Autocomplete,
  TextField,
  IconButton,
  Tooltip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import apiService from '../../../../core/services/api-client';

interface Product {
  product_sku: string;
  product_name: string;
  store_name: string;
  physical_stock: number;
  metric_value: number;
  cost: number;  // ✅ Campo correcto para mostrar el costo
  recommended_units: number | null;  // ✅ NUEVO: Unidades recomendadas
  recommended_value: number | null;  // ✅ NUEVO: Valor total recomendado
  adjustment_type: 'entrada' | 'salida' | 'neutral' | null;  // ✅ NUEVO: Tipo de ajuste (solo para AJUSTE_STOCK)
  report_date: string;  // Fecha en que se generó el reporte
  snapshot_date: string;  // ✅ Fecha real del snapshot (la que debe mostrarse)
  suggestion: {
    suggested_action: string;
    suggested_quantity?: number;
    suggested_priority: number;
    reason: string;
  };
}

interface TotalsExplanation {
  total_units: string;
  total_value: string;
}

interface AvailableFilters {
  products: string[];
  skus: string[];
  warehouses: string[];
  dates: string[];
  suppliers: string[];
}

interface AppliedFilters {
  product: string | null;
  sku: string | null;
  warehouse: string | null;
  date: string | null;
  supplier: string | null;
}

interface MetricDetails {
  metric_type: string;
  total_products: number;
  total_units: number;
  total_value: number;
  products: Product[];
  suggestions_summary: Record<string, number>;
  totals_explanation?: TotalsExplanation;
}

const MetricDetailsDashboard = () => {
  const { id: companyId, metricType, startDate, endDate } = useParams<{ 
    id: string; 
    metricType: string;
    startDate: string;
    endDate: string;
  }>();
  const navigate = useNavigate();
  const [data, setData] = useState<MetricDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [integrationId, setIntegrationId] = useState<number | null>(null);
  
  // Estados para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(500);  // ✅ Aumentado de 100 a 500
  const [totalItems, setTotalItems] = useState(0);
  
  // Estados para filtros
  const [availableFilters, setAvailableFilters] = useState<AvailableFilters | null>(null);
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilters>({
    product: null,
    sku: null,
    warehouse: null,
    date: null,
    supplier: null
  });
  
  // Estado para descarga de Excel
  const [isDownloading, setIsDownloading] = useState(false);

  // ✅ Determinar si es una métrica de acción (para mostrar columnas adicionales)
  const isActionMetric = metricType ? [
    'RECOMENDACION_COMPRA',
    'DEVOLUCIONES',
    'AJUSTE_STOCK',
    'VENTA_SIN_STOCK'
  ].includes(metricType) : false;

  // ✅ Mapeo de nombres de columnas según el tipo de métrica
  const columnNames: Record<string, { units: string; value: string }> = {
    'RECOMENDACION_COMPRA': {
      units: 'Unidades Recomendadas',
      value: 'Costo Total de Productos Recomendados'
    },
    'DEVOLUCIONES': {
      units: 'Unidades Devueltas',
      value: 'Valor Total de Unidades Devueltas'
    },
    'AJUSTE_STOCK': {
      units: 'Unidades Ajustadas (Entrada/Salida)',
      value: 'Impacto en Valor de Inventario'
    },
    'VENTA_SIN_STOCK': {
      units: 'Unidades Vendidas Sin Stock',
      value: 'Costo Total de Unidades Sin Stock'
    }
  };

  // Obtener nombres de columnas para la métrica actual
  const currentColumnNames = metricType && columnNames[metricType] 
    ? columnNames[metricType] 
    : { units: 'Unidades', value: 'Valor Total' };

  // Obtener información de la compañía para determinar el endpoint correcto
  useEffect(() => {
    const fetchCompanyInfo = async () => {
      try {
        const response = await apiService().get(`/companies/${companyId}`);
        const company = response.data;
        setIntegrationId(company.integration?.id || 1); // Por defecto Bsale
        console.log('🏢 Company info:', { 
          id: company.id, 
          name: company.name, 
          integrationId: company.integration?.id 
        });
      } catch (err) {
        console.error('❌ Error al obtener info de compañía:', err);
        setIntegrationId(1); // Por defecto Bsale si falla
      }
    };
    
    if (companyId) {
      fetchCompanyInfo();
    }
  }, [companyId]);

  useEffect(() => {
    if (integrationId !== null) {
      fetchAvailableFilters();
    }
  }, [companyId, metricType, startDate, endDate, integrationId]);

  useEffect(() => {
    if (integrationId !== null) {
      setCurrentPage(1);  // Resetear a página 1 cuando cambien los filtros
      fetchMetricDetails();
    }
  }, [companyId, metricType, startDate, endDate, appliedFilters, integrationId]);

  useEffect(() => {
    if (integrationId !== null && currentPage > 1) {
      console.log('=== PAGINACIÓN: Cambio de página ===');
      console.log('Página actual:', currentPage);
      console.log('Offset:', (currentPage - 1) * itemsPerPage);
      console.log('Limit:', itemsPerPage);
      console.log('====================================');
      fetchMetricDetails();  // Cargar datos cuando cambie la página
    }
  }, [currentPage]);

  const fetchAvailableFilters = async () => {
    try {
      console.log('🔍 Cargando filtros disponibles...');

      if (!startDate || !endDate || integrationId === null) {
        return;
      }

      // Determinar el endpoint según integration_id
      const prefix = integrationId === 2 ? 'skualo-metrics' : 'bsale-metrics';
      console.log('🔍 Usando endpoint:', `/${prefix}/${companyId}/metric-alerts/${metricType}/filters`);

      // Usar un rango amplio para obtener TODAS las fechas disponibles (desde agosto 2025)
      const filtersStartDate = '2025-08-01';
      const filtersEndDate = endDate;

      const response = await apiService().get(
        `/${prefix}/${companyId}/metric-alerts/${metricType}/filters`,
        {
          params: {
            start_date: filtersStartDate,
            end_date: filtersEndDate
          }
        }
      );

      console.log('✅ Filtros disponibles:', response.data.filters);
      setAvailableFilters(response.data.filters);
    } catch (err: any) {
      console.error('❌ Error al obtener filtros:', err);
      // No mostrar error si los filtros fallan, solo log
    }
  };

  const fetchMetricDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔍 Cargando detalles de métrica:', metricType, 'para companyId:', companyId);
      console.log('📅 Fechas:', { startDate, endDate });
      console.log('🔎 Filtros aplicados:', appliedFilters);
      console.log('🔍 Integration ID:', integrationId);

      // Validar que las fechas estén presentes
      if (!startDate || !endDate) {
        throw new Error('Las fechas son obligatorias para consultar el detalle');
      }

      if (integrationId === null) {
        console.log('⏳ Esperando integration_id...');
        return;
      }

      // Determinar el endpoint según integration_id
      const prefix = integrationId === 2 ? 'skualo-metrics' : 'bsale-metrics';
      console.log('🔍 Usando endpoint:', `/${prefix}/${companyId}/metric-alerts/${metricType}/details`);

      // Construir parámetros con filtros y paginación
      const offset = (currentPage - 1) * itemsPerPage;
      const params: any = {
        start_date: startDate,
        end_date: endDate,
        limit: itemsPerPage,  // ✅ 500 productos por página
        offset: offset
      };

      // Agregar filtros activos
      if (appliedFilters.product) params.filter_product = appliedFilters.product;
      if (appliedFilters.sku) params.filter_sku = appliedFilters.sku;
      if (appliedFilters.warehouse) params.filter_warehouse = appliedFilters.warehouse;
      if (appliedFilters.date) params.filter_date = appliedFilters.date;
      if (appliedFilters.supplier) params.filter_supplier = appliedFilters.supplier;

      console.log('=== PAGINACIÓN: Parámetros de llamada ===');
      console.log('Página actual:', currentPage);
      console.log('Limit:', itemsPerPage);
      console.log('Offset:', offset);
      console.log('Rango de productos:', `${offset + 1} - ${offset + itemsPerPage}`);
      console.log('Parámetros completos:', params);
      console.log('=========================================');

      const response = await apiService().get(
        `/${prefix}/${companyId}/metric-alerts/${metricType}/details`,
        { params }
      );

      console.log('✅ Respuesta de detalles:', response.data);
      
      // 🔍 LOGS DE DEPURACIÓN - Verificar fechas recibidas
      if (response.data.products && response.data.products.length > 0) {
        console.log('=== DEBUG: Productos recibidos ===');
        console.log('Total productos:', response.data.products.length);
        console.log('Primer producto:', response.data.products[0]);
        console.log('snapshot_date:', response.data.products[0].snapshot_date);
        console.log('report_date:', response.data.products[0].report_date);
        console.log('================================');
      }
      
      console.log('=== PAGINACIÓN: Respuesta del backend ===');
      console.log('Total de productos (total):', response.data.total || response.data.total_products);
      console.log('Productos en esta página:', response.data.products?.length || 0);
      console.log('Limit usado:', response.data.limit);
      console.log('Offset usado:', response.data.offset);
      console.log('Total de páginas:', Math.ceil((response.data.total || response.data.total_products) / itemsPerPage));
      console.log('==========================================');
      
      setData(response.data);
      setTotalItems(response.data.total || response.data.total_products);  // ✅ Guardar total para paginación
    } catch (err: any) {
      console.error('❌ Error al obtener detalles:', err);
      console.error('❌ Respuesta del servidor:', err.response?.data);
      setError(err.response?.data?.detail || err.message || 'Error al obtener detalles de la métrica');
    } finally {
      setLoading(false);
    }
  };

  const getMetricTitle = (type: string): string => {
    const titles: Record<string, string> = {
      'STOCK_CERO': 'Stock Cero',
      'BAJA_ROTACION': 'Baja Rotación',
      'STOCK_CRITICO': 'Stock Crítico',
      'SOBRE_STOCK': 'Sobre Stock',
      'RECOMENDACION_COMPRA': 'Recomendación de Compra',
      'VENTA_SIN_STOCK': 'Venta Sin Stock',
      'DEVOLUCIONES': 'Devoluciones',
      'AJUSTE_STOCK': 'Ajuste de Stock'
    };
    return titles[type] || type;
  };

  const getActionColor = (action: string): "error" | "warning" | "info" | "success" => {
    if (action.includes('URGENTE')) return 'error';
    if (action.includes('COMPRAR')) return 'warning';
    if (action.includes('TRANSFERIR')) return 'info';
    return 'success';
  };

  const formatCurrency = (amount: number): string => {
    // Truncar decimales (no redondear) para mejor legibilidad
    const truncatedAmount = Math.trunc(amount);
    
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(truncatedAmount);
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return '-';
    
    // ✅ Usar el string directamente sin parsear a Date para evitar problemas de zona horaria
    // Formato esperado del backend: "2026-03-01" (YYYY-MM-DD)
    // Formato deseado: "01-03-2026" (DD-MM-YYYY)
    
    const parts = dateString.split('T')[0].split('-');  // Tomar solo la parte de fecha (antes de T si existe)
    if (parts.length === 3) {
      const [year, month, day] = parts;
      return `${day}-${month}-${year}`;  // DD-MM-YYYY
    }
    
    // Fallback: si el formato no es el esperado, retornar el string original
    return dateString;
  };

  const getMensajeSinResultados = (): string => {
    // Contar filtros activos
    const filtrosActivos = Object.values(appliedFilters).filter(v => v !== null && v !== '').length;
    
    if (filtrosActivos > 1) {
      // Múltiples filtros activos pero sin resultados = filtros incompatibles
      return "Los filtros seleccionados no coinciden entre sí";
    } else if (filtrosActivos === 1) {
      // Un solo filtro activo sin resultados
      return "No hay productos para mostrar";
    } else {
      // Sin filtros y sin productos
      return "No hay productos disponibles para esta métrica";
    }
  };

  const handleFilterChange = (filterName: keyof AppliedFilters, value: string | null) => {
    setAppliedFilters({
      ...appliedFilters,
      [filterName]: value
    });
  };

  const clearFilter = (filterName: keyof AppliedFilters) => {
    handleFilterChange(filterName, null);
  };

  const clearAllFilters = () => {
    setAppliedFilters({
      product: null,
      sku: null,
      warehouse: null,
      date: null,
      supplier: null
    });
  };

  const hasActiveFilters = Object.values(appliedFilters).some(v => v !== null);

  const handleDownloadExcel = async () => {
    setIsDownloading(true);
    
    try {
      console.log('=== INICIO DESCARGA EXCEL ===');
      console.log('Company ID:', companyId);
      console.log('Metric Type:', metricType);
      console.log('Start Date:', startDate);
      console.log('End Date:', endDate);
      console.log('Integration ID:', integrationId);
      
      // Validar fechas requeridas
      if (!startDate || !endDate) {
        throw new Error('Las fechas son obligatorias para exportar');
      }
      
      // Construir parámetros (start_date y end_date son REQUERIDOS)
      const params: any = {
        start_date: startDate,  // REQUERIDO: formato YYYY-MM-DD
        end_date: endDate,      // REQUERIDO: formato YYYY-MM-DD
      };
      
      // Agregar filtros opcionales solo si tienen valor
      if (appliedFilters.product) params.filter_product = appliedFilters.product;
      if (appliedFilters.sku) params.filter_sku = appliedFilters.sku;
      if (appliedFilters.warehouse) params.filter_warehouse = appliedFilters.warehouse;
      if (appliedFilters.date) params.filter_date = appliedFilters.date;
      if (appliedFilters.supplier) params.filter_supplier = appliedFilters.supplier;
      
      console.log('📊 Parámetros de descarga:', params);
      
      // Determinar el endpoint según integration_id
      const prefix = integrationId === 2 ? 'skualo-metrics' : 'bsale-metrics';
      console.log('🔍 Prefijo del endpoint:', prefix);
      
      console.log('📡 Haciendo petición con apiService...');
      
      // ✅ USAR apiService con responseType: 'blob'
      const response = await apiService().get(
        `/${prefix}/${companyId}/metric-alerts/${metricType}/export`,
        {
          params,
          responseType: 'blob'
        }
      );
      
      console.log('📥 Response status:', response.status);
      console.log('📥 Response headers:', response.headers);
      
      // Obtener el archivo como Blob
      const blob = response.data;
      console.log('📦 Blob size:', blob.size, 'bytes');
      console.log('📦 Blob type:', blob.type);
      
      if (blob.size === 0) {
        throw new Error('El archivo descargado está vacío');
      }
      
      // Obtener el nombre del archivo desde los headers
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'reporte.xlsx'; // Fallback por defecto
      
      if (contentDisposition) {
        console.log('📋 Content-Disposition:', contentDisposition);
        
        // ✅ REGEX CORRECTO: Maneja correctamente las comillas
        // Formato esperado: attachment; filename="Nombre_Archivo.xlsx"
        const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
        console.log('🔍 Regex match:', filenameMatch);
        
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
          console.log('✅ Nombre extraído:', filename);
        } else {
          console.warn('⚠️ No se pudo extraer el nombre del archivo');
          console.warn('Content-Disposition completo:', contentDisposition);
        }
      }
      
      console.log('📄 Nombre final del archivo:', filename);
      
      // Descargar el archivo
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      
      // Limpiar
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);
      
      console.log('=== DESCARGA EXITOSA ===');
      
    } catch (error: any) {
      console.error('=== ERROR EN DESCARGA ===');
      console.error('Error type:', error.constructor.name);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      console.error('Error completo:', error);
      
      // Mostrar mensaje de error específico al usuario
      const errorMessage = error.message || 'Error desconocido al descargar el archivo';
      alert(`Error al descargar el archivo:\n\n${errorMessage}\n\nRevisa la consola para más detalles.`);
      
    } finally {
      setIsDownloading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <div className="w-full p-24">
        <Box display="flex" alignItems="center" mb={3}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(`/dashboard/metrics/${companyId}`)}
            sx={{ mr: 2, color: "#000000" }}
          >
            Volver
          </Button>
          <Typography variant="h4">
            Error al cargar detalles
          </Typography>
        </Box>
        
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        
        <Alert severity="info">
          <Typography variant="body2" gutterBottom>
            <strong>Posibles causas:</strong>
          </Typography>
          <Typography variant="body2" component="div">
            • El endpoint del backend aún no está implementado<br />
            • No hay datos disponibles para esta métrica<br />
            • El tipo de métrica no es válido: {metricType}<br />
            • Las fechas no fueron proporcionadas correctamente
          </Typography>
          <Typography variant="body2" sx={{ mt: 2 }}>
            <strong>Endpoint esperado:</strong> GET /skualo-metrics/{companyId}/metric-alerts/{metricType}/details?start_date={startDate}&end_date={endDate}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>Fechas recibidas:</strong> {startDate} - {endDate}
          </Typography>
        </Alert>
        
        <Button
          variant="contained"
          onClick={fetchMetricDetails}
          sx={{ mt: 2 }}
        >
          Reintentar
        </Button>
      </div>
    );
  }

  if (!data) {
    return (
      <Box p={3}>
        <Alert severity="info">No hay datos disponibles</Alert>
      </Box>
    );
  }

  return (
    <div className="w-full p-24">
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center">
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(`/dashboard/metrics/${companyId}`)}
            sx={{ mr: 2, color: "#000000" }}
          >
            Volver
          </Button>
          <Typography variant="h4">
            Detalle: {getMetricTitle(data.metric_type)} ({data.total_products} productos)
          </Typography>
        </Box>
        
        {/* Botón de Descarga */}
        <Tooltip title={isDownloading ? "Descargando..." : "Descargar Excel"}>
          <IconButton
            onClick={handleDownloadExcel}
            disabled={isDownloading}
            sx={{
              backgroundColor: '#000000',
              color: 'white',
              width: 48,
              height: 48,
              '&:hover': {
                backgroundColor: '#333333',
              },
              '&:disabled': {
                backgroundColor: '#9ca3af',
                color: 'white',
              },
            }}
          >
            {isDownloading ? (
              <CircularProgress size={24} sx={{ color: 'white' }} />
            ) : (
              <DownloadIcon />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Panel de Filtros */}
      {availableFilters && (
        <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, color: '#374151' }}>
            Filtros
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            {/* Filtro por Producto */}
            <Autocomplete
              options={availableFilters.products}
              value={appliedFilters.product}
              onChange={(e, value) => handleFilterChange('product', value)}
              renderInput={(params) => (
                <TextField {...params} label="Producto" size="small" />
              )}
              sx={{ minWidth: 250 }}
            />

            {/* Filtro por SKU */}
            <Autocomplete
              options={availableFilters.skus}
              value={appliedFilters.sku}
              onChange={(e, value) => handleFilterChange('sku', value)}
              renderInput={(params) => (
                <TextField {...params} label="SKU" size="small" />
              )}
              sx={{ minWidth: 200 }}
            />

            {/* Filtro por Bodega */}
            <Autocomplete
              options={availableFilters.warehouses}
              value={appliedFilters.warehouse}
              onChange={(e, value) => handleFilterChange('warehouse', value)}
              renderInput={(params) => (
                <TextField {...params} label="Bodega" size="small" />
              )}
              sx={{ minWidth: 200 }}
            />

            {/* Filtro por Fecha */}
            <Autocomplete
              options={availableFilters.dates}
              value={appliedFilters.date}
              onChange={(e, value) => handleFilterChange('date', value)}
              renderInput={(params) => (
                <TextField {...params} label="Fecha" size="small" />
              )}
              sx={{ minWidth: 150 }}
              getOptionLabel={(option) => formatDate(option)}
            />

            {/* Filtro por Proveedor */}
            {availableFilters.suppliers && availableFilters.suppliers.length > 0 && (
              <Autocomplete
                options={availableFilters.suppliers}
                value={appliedFilters.supplier}
                onChange={(e, value) => handleFilterChange('supplier', value)}
                renderInput={(params) => (
                  <TextField {...params} label="Proveedor" size="small" />
                )}
                sx={{ minWidth: 200 }}
              />
            )}
          </Box>

          {/* Chips de filtros activos */}
          {hasActiveFilters && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {appliedFilters.product && (
                <Chip
                  label={`Producto: ${appliedFilters.product}`}
                  onDelete={() => clearFilter('product')}
                  color="primary"
                  size="small"
                />
              )}
              {appliedFilters.sku && (
                <Chip
                  label={`SKU: ${appliedFilters.sku}`}
                  onDelete={() => clearFilter('sku')}
                  color="primary"
                  size="small"
                />
              )}
              {appliedFilters.warehouse && (
                <Chip
                  label={`Bodega: ${appliedFilters.warehouse}`}
                  onDelete={() => clearFilter('warehouse')}
                  color="primary"
                  size="small"
                />
              )}
              {appliedFilters.date && (
                <Chip
                  label={`Fecha: ${formatDate(appliedFilters.date)}`}
                  onDelete={() => clearFilter('date')}
                  color="primary"
                  size="small"
                />
              )}
              {appliedFilters.supplier && (
                <Chip
                  label={`Proveedor: ${appliedFilters.supplier}`}
                  onDelete={() => clearFilter('supplier')}
                  color="primary"
                  size="small"
                />
              )}
              <Chip
                label="Limpiar todos"
                onClick={clearAllFilters}
                color="default"
                size="small"
              />
            </Box>
          )}
        </Box>
      )}

      {/* Resumen */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Total de Productos
              </Typography>
              <Typography variant="h3" color="primary">
                {data.total_products}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Total de Unidades
              </Typography>
              <Typography variant="h3" color="secondary">
                {data.total_units.toLocaleString('es-CL')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Valor Total
              </Typography>
              <Typography variant="h3" color="success.main">
                {formatCurrency(data.total_value || 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Cuadro de ayuda con explicaciones */}
      {data.totals_explanation && (
        <Box
          sx={{
            backgroundColor: '#f3f4f6',
            border: '1px solid #d1d5db',
            borderRadius: 2,
            p: 2,
            mb: 3,
          }}
        >
          <Box display="flex" alignItems="flex-start" gap={1}>
            <Typography sx={{ fontSize: '20px', color: '#2563eb' }}>ℹ️</Typography>
            <Box flex={1}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#374151', mb: 1 }}>
                Información sobre los totales
              </Typography>
              <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                <Box component="li" sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                  <Typography sx={{ color: '#9ca3af', fontSize: '14px' }}>•</Typography>
                  <Typography variant="body2" sx={{ color: '#4b5563' }}>
                    <strong>Total de Unidades:</strong> {data.totals_explanation.total_units}
                  </Typography>
                </Box>
                <Box component="li" sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Typography sx={{ color: '#9ca3af', fontSize: '14px' }}>•</Typography>
                  <Typography variant="body2" sx={{ color: '#4b5563' }}>
                    <strong>Valor Total:</strong> {data.totals_explanation.total_value}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {/* Tabla de Productos */}
      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Fecha</TableCell>
                  <TableCell>SKU</TableCell>
                  <TableCell>Producto</TableCell>
                  <TableCell>Bodega</TableCell>
                  <TableCell align="right">Stock</TableCell>
                  {/* ✅ NUEVA COLUMNA 1: Solo para métricas de acción - Nombre dinámico */}
                  {isActionMetric && (
                    <TableCell align="right">{currentColumnNames.units}</TableCell>
                  )}
                  <TableCell align="right">Costo</TableCell>
                  {/* ✅ NUEVA COLUMNA 2: Solo para métricas de acción - Nombre dinámico */}
                  {isActionMetric && (
                    <TableCell align="right">{currentColumnNames.value}</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.products.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isActionMetric ? 8 : 6} align="center">
                      <Typography variant="body2" color="text.secondary">
                        {getMensajeSinResultados()}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  // Ordenar por fecha descendente (más reciente primero)
                  (() => {
                    const sortedProducts = [...data.products].sort((a, b) => {
                      const dateA = new Date(a.snapshot_date || a.report_date || '').getTime();
                      const dateB = new Date(b.snapshot_date || b.report_date || '').getTime();
                      return dateB - dateA; // Descendente
                    });
                    
                    // 🔍 LOG: Verificar qué se va a renderizar
                    if (sortedProducts.length > 0) {
                      console.log('=== DEBUG: Renderizando tabla ===');
                      console.log('Total de productos a renderizar:', sortedProducts.length);
                      console.log('Primer producto a renderizar:', sortedProducts[0]);
                      console.log('Último producto a renderizar:', sortedProducts[sortedProducts.length - 1]);
                      console.log('Fecha que se mostrará (primero):', sortedProducts[0].snapshot_date || sortedProducts[0].report_date);
                      console.log('Fecha formateada (primero):', formatDate(sortedProducts[0].snapshot_date || sortedProducts[0].report_date));
                      console.log('================================');
                    }
                    
                    return sortedProducts.map((product, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(product.snapshot_date || product.report_date)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {product.product_sku}
                          </Typography>
                        </TableCell>
                        <TableCell>{product.product_name}</TableCell>
                        <TableCell>{product.store_name}</TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {product.physical_stock}
                          </Typography>
                        </TableCell>
                        {/* ✅ NUEVA COLUMNA 1: Unidades (con lógica especial para AJUSTE_STOCK) */}
                        {isActionMetric && (
                          <TableCell align="right">
                            {metricType === 'AJUSTE_STOCK' ? (
                              // Lógica especial para AJUSTE_STOCK (Opción B - Con indicador)
                              <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
                                <Typography variant="body2" fontWeight="bold">
                                  {product.recommended_units !== null && product.recommended_units !== undefined
                                    ? product.recommended_units.toLocaleString('es-CL')
                                    : '-'}
                                </Typography>
                                {product.adjustment_type === 'entrada' && (
                                  <Typography variant="body2" fontWeight="bold" sx={{ color: 'success.main' }}>
                                    ↑ Entrada
                                  </Typography>
                                )}
                                {product.adjustment_type === 'salida' && (
                                  <Typography variant="body2" fontWeight="bold" sx={{ color: 'error.main' }}>
                                    ↓ Salida
                                  </Typography>
                                )}
                                {product.adjustment_type === 'neutral' && (
                                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    → Neutral
                                  </Typography>
                                )}
                              </Box>
                            ) : (
                              // Lógica normal para otras métricas de acción
                              <Typography variant="body2" fontWeight="bold" color="primary">
                                {product.recommended_units !== null && product.recommended_units !== undefined
                                  ? product.recommended_units.toLocaleString('es-CL')
                                  : '-'}
                              </Typography>
                            )}
                          </TableCell>
                        )}
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">
                            {formatCurrency(product.cost || 0)}
                          </Typography>
                        </TableCell>
                        {/* ✅ NUEVA COLUMNA 2: Valor Total Recomendado */}
                        {isActionMetric && (
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="bold" color="success.main">
                              {product.recommended_value !== null && product.recommended_value !== undefined
                                ? formatCurrency(product.recommended_value)
                                : '-'}
                            </Typography>
                          </TableCell>
                        )}
                      </TableRow>
                    ));
                  })()
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Controles de Paginación */}
          {totalItems > itemsPerPage && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: 2,
                pt: 2,
                borderTop: '1px solid #e0e0e0'
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Mostrando {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, totalItems)} de {totalItems} productos
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  Anterior
                </Button>
                
                <Typography variant="body2" sx={{ mx: 2 }}>
                  Página {currentPage} de {Math.ceil(totalItems / itemsPerPage)}
                </Typography>
                
                <Button
                  variant="outlined"
                  size="small"
                  disabled={currentPage >= Math.ceil(totalItems / itemsPerPage)}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  Siguiente
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MetricDetailsDashboard;
